import TimeSpan from '../TimeSpan.js';
import RecurrenceModel from '../RecurrenceModel.js';
import ArrayHelper from '../../../Core/helper/ArrayHelper.js';
import DateHelper from '../../../Core/helper/DateHelper.js';
import AbstractRecurrenceIterator from '../../data/util/recurrence/AbstractRecurrenceIterator.js';
import RecurrenceDayRuleEncoder from '../../data/util/recurrence/RecurrenceDayRuleEncoder.js';

function convertExceptionDatesValue(value) {
    const
        result         = {},
        { dateFormat } = this;

    if (value) {
        value = typeof value == 'string' ? value.split(',') : ArrayHelper.asArray(value);

        value.forEach(item => {
            if (typeof item == 'string') {
                item = DateHelper.parse(item, dateFormat);
            }

            // If we've got a valid date out of the incoming item, add an exception key
            if (!isNaN(item)) {
                result[DateHelper.makeKey(item)] = 1;
            }
        });
    }

    return result;
}

function serializeExceptionDatesValue(value) {
    const
        result         = [],
        { dateFormat } = this;

    for (const date in value) {
        if (value[date]) {
            result.push(DateHelper.format(DateHelper.parseKey(date), dateFormat));
        }
    }

    return result;
}

const emptyArray = [];

/**
 * @module Scheduler/model/mixin/RecurringTimeSpan
 */

/**
 * This mixin class provides recurrence related fields and methods to a {@link Scheduler.model.TimeSpan} model. This
 * class is used to support recurring events (and time ranges) in the Scheduler and Calendar components.
 *
 * ## Data structure
 *
 * The mixin introduces two types of timespans: the __recurring timespan__ and its __occurrences__.
 * A __recurring timespan__ is a timespan having a {@link #field-recurrenceRule} specified and
 * its __occurrences__ are "fake" dynamically generated timespans. You can also provide {@link #field-exceptionDates}
 * to the data to exclude certain dates. See sample data below,
 *
 * ```javascript
 * {
 *     id: 7,
 *     startDate: '2021-10-12T14:00:00',
 *     endDate: '2021-10-12T15:00:00',
 *     name: 'Lunch',
 *     resourceId: 'hotel',
 *     recurrenceRule: 'FREQ=DAILY;COUNT=5',
 *     exceptionDates: ['2021-10-14']
 * }
 * ```
 *
 * ## Useful API methods
 *
 * There are few methods allowing to distinguish a recurring event and an occurrence: {@link #property-isRecurring}, {@link #property-isOccurrence}
 * and {@link #property-recurringTimeSpan} (returns the event this record is an occurrence of).
 *
 * The {@link #field-recurrenceRule recurrence rule} defined for the event is parsed and
 * represented with {@link Scheduler.model.RecurrenceModel} class (can be changed by setting {@link #property-recurrenceModel} property) instance.
 * See: {@link #property-recurrence} property.
 *
 * To query the ocurrences for a specific timespan, use the {@link #function-getOccurrencesForDateRange} method:
 *
 * ```
 * // Get all occurrences between Feb 1-10
 * timespan.getOccurrencesForDateRange(new Date(2024, 1, 1), new Date(2024, 1, 10));
 * ```
 *
 * @mixin
 * @mixinbase TimeSpan
 */
export default Target => class RecurringTimeSpan extends (Target || TimeSpan) {
    static $name = 'RecurringTimeSpan';

    /**
     * Returns `true` if this timespan supports recurring.
     * @property {Boolean}
     * @category Recurrence
     */
    get supportsRecurring() {
        return true;
    }

    static fields = [
        /**
         * The timespan recurrence rule. A string in [RFC-5545](https://tools.ietf.org/html/rfc5545#section-3.3.10)
         * described format ("RRULE" expression).
         * @field {String} recurrenceRule
         * @category Scheduling
         */
        {
            name     : 'recurrenceRule',
            internal : true
        },
        /**
         * A string (either a single date or multiple dates separated by comma) or an array of strings containing
         * the timespan exception dates. The dates that must be skipped when generating occurrences for a repeating
         * timespan. This is used to modify only individual occurrences of the timespan so the further regenerations
         * won't create another copy of this occurrence again.
         *
         * ```javascript
         * {
         *     id: 7,
         *     startDate: '2021-10-12T14:00:00',
         *     endDate: '2021-10-12T15:00:00',
         *     name: 'Lunch',
         *     resourceId: 'hotel',
         *     recurrenceRule: 'FREQ=DAILY;COUNT=5',
         *     exceptionDates: ['2021-10-14']
         * }
         * ```
         *
         * Use {@link #function-addExceptionDate} method to add an individual entry to the dates array:
         *
         * ```javascript
         * // Break the link between the occurrence and its base.
         * // This also adds the occurrence date as an exception date
         * // so that the base timespan knows that this date should be skipped when regenerating its occurrences.
         * occurrence.recurringTimeSpan = null;
         *
         * // now the occurrence is an individual record that can be changed & persisted freely
         * occurrence.setStartEndDate(new Date(2018, 6, 2), new Date(2018, 6, 3));
         * ```
         * **Note:** The dates in this field get automatically removed when the event changes its {@link Scheduler.model.TimeSpan#field-startDate start date}.
         *
         * @field {String|String[]} exceptionDates
         * @category Scheduling
         */
        {
            name      : 'exceptionDates',
            convert   : convertExceptionDatesValue,
            serialize : serializeExceptionDatesValue,
            internal  : true
        }
    ];

    /**
     * Override of {@link Core/data/Model}'s method. If an {@link #property-isOccurrence} is passed, it is detached from
     * its parent recurring event. If it still has a recurrence then the recurring event is changed to stop at the
     * occurrence date. If it has no recurrence an exception is added at the occurrence date.
     * @category Recurrence
     */
    remove(...args) {
        if (this.isOccurrence) {
            const { recurringTimeSpan } = this;

            this.cancelBatch();
            recurringTimeSpan.beginBatch();
            this.detachFromRecurringEvent();
            recurringTimeSpan.endBatch();
        }
        else {
            return super.remove(...args);
        }
    }

    get eventStore() {
        let result = (this.isOccurrence && this.recurringEvent?.eventStore) || super.eventStore;

        // Recurrence methods are called on `eventStore`, but in case we have a regular
        // store with recurrence mixin, we should return own store instead
        if (!result && this.firstStore?.isRecurringTimeSpansMixin) {
            result = this.firstStore;
        }

        return result;
    }

    /**
     * Name of the class representing the recurrence model, defaults to {@link Scheduler.model.RecurrenceModel}
     * @property {String}
     * @category Recurrence
     */
    get recurrenceModel() {
        return this._recurrenceModel || RecurrenceModel;
    }

    set recurrenceModel(model) {
        this._recurrenceModel = model;
    }

    /**
     * Sets a recurrence for the timespan with a given frequency, interval, and end.
     * @param {RecurrenceModelConfig|String|Scheduler.model.RecurrenceModel} recurrence A data object for an instance of
     * {@link Scheduler.model.RecurrenceModel}. May also be the frequency string: `DAILY`, `WEEKLY`, `MONTHLY`, or
     * `YEARLY`.
     *
     * ```javascript
     * // repeat the event every other week till Jan 2 2039
     * event.setRecurrence({
     *     frequency : "WEEKLY",
     *     interval  : 2,
     *     endDate   : new Date(2039, 0, 2)
     * });
     * ```
     *
     * Also a {@link Scheduler.model.RecurrenceModel recurrence model} can be provided as the only argument for this
     * method:
     *
     * ```javascript
     * const recurrence = new RecurrenceModel({ frequency : 'DAILY', interval : 5 });
     *
     * event.setRecurrence(recurrence);
     * ```
     *
     * or
     *
     * ```javascript
     * event.setRecurrence("WEEKLY", 2, new Date(2039, 0, 2));
     * ```
     * @param {Number} [interval] The interval between occurrences (instances of this recurrence). For example, a daily
     * recurrence with an interval of 2 occurs every other day. Must be greater than 0.
     * @param {Number|Date} [recurrenceEnd] The end of the recurrence. The value can be specified by a date or by a
     * maximum count of occurrences (has to greater than 1, since 1 means the event itself).
     * @category Recurrence
     */
    setRecurrence(recurrence, interval, recurrenceEnd) {
        const me = this;

        let recurrenceRule;

        if (recurrence) {
            if (!recurrence.isRecurrenceModel) {
                if (typeof recurrence === 'string') {
                    recurrence = {
                        frequency : recurrence
                    };

                    if (interval) {
                        recurrence.interval = interval;
                    }

                    // If the recurrence is limited
                    if (recurrenceEnd) {
                        if (recurrenceEnd instanceof Date) {
                            recurrence.endDate = recurrenceEnd;
                        }
                        else {
                            recurrence.count = recurrenceEnd;
                        }
                    }
                }
                recurrence = new me.recurrenceModel(recurrence);
            }

            // The RecurrenceModel has a reference to its owning recurring event.
            // It uses this to remove the owning event's exceptions after its new endDate
            // whenever its endDate is changed.
            recurrence.timeSpan = me;

            recurrenceRule = recurrence.rule;
        }

        me.recurrence     = recurrence;
        me.recurrenceRule = recurrenceRule;
    }

    /**
     * The recurrence model used for the timespan.
     * @property {Scheduler.model.RecurrenceModel}
     * @category Recurrence
     */
    get recurrence() {
        const
            me = this,
            rule = me.recurrenceRule;

        if (!me._recurrence && rule) {
            me._recurrence = new me.recurrenceModel({ rule, timeSpan : me, id : `${me.id}-recurrence` });
        }

        return me._recurrence;
    }

    set recurrence(recurrence) {
        const me = this;

        me._recurrence = recurrence;

        if (recurrence) {
            // bind recurrence instance to the model
            recurrence.timeSpan = me;
            me.recurrenceRule   = recurrence.rule;
        }
        else {
            // If this is being done to an occurrence, it's a signal that we are being
            // mutated into an exception. Apply the change immediately, directly to the data.
            if (me.isOccurrence) {
                me.setData('recurrenceRule', null);
            }
            else {
                me.recurrenceRule = null;
            }
        }
    }

    /**
     * Indicates if the timespan is recurring.
     * @property {Boolean}
     * @readonly
     * @category Recurrence
     */
    get isRecurring() {
        // MUST evaluate in this order so that if it is an occurrence,
        // the recurrence getter does not refresh the rule
        return Boolean(!this.isOccurrence && this.recurrence);
    }

    /**
     * Indicates if the timespan is an occurrence of another recurring timespan.
     * @property {Boolean}
     * @readonly
     * @category Recurrence
     */
    get isOccurrence() {
        return Boolean(this.recurringTimeSpan);
    }

    /**
     * The "main" timespan this model is an occurrence of. For non-occurrences returns `null`.
     * @property {Scheduler.model.TimeSpan}
     * @readonly
     * @internal
     * @category Recurrence
     */
    get recurringTimeSpan() {
        return this._recurringTimeSpan;
    }

    get isPersistable() {
        return super.isPersistable && (!this.supportsRecurring || !this.isOccurrence);
    }

    set recurringTimeSpan(recurringTimeSpan) {
        this._recurringTimeSpan = recurringTimeSpan;
    }

    /**
     * Returns the occurrences of this event over the specified time range. If the first
     * occurrence is in the time range `*this*` record is included in that position.
     * @param {Date} startDate The start date of the range for which to include occurrences.
     * @param {Date} [endDate] The end date of the range for which to include occurrences.
     * Defaults to the startDate.
     * @returns {Scheduler.model.TimeSpan[]} The array of occurrences which occur over the specified range.
     * @category Recurrence
     */
    getOccurrencesForDateRange(startDate, endDate = startDate) {
        return this.eventStore.getOccurrencesForTimeSpan(this, startDate, endDate);
    }

    /**
     * Array of this recurring timespan's cached occurrences. __Not including the owning recurring
     * event__.
     *
     * Empty if the timespan is not recurring.
     *
     * __Note that this is an internal accessor and is cleared whenever changes are made to the
     * owning recurring event__.
     * @property {Scheduler.model.TimeSpan[]}
     * @readonly
     * @internal
     * @category Recurrence
     */
    get occurrences() {
        if (this.isRecurring) {
            const result = [];

            // The occurrencesMap contains entries for each occurrence date.
            this.occurrenceMap.forEach(occurrence => {
                if (occurrence !== this) {
                    result.push(occurrence);
                }
            });

            return result;
        }

        return emptyArray;
    }

    /**
     * A Map, keyed by each date an occurrence intersects, of occurrences of this event.
     * @property {Map}
     * @readonly
     * @internal
     * @category Recurrence
     */
    get occurrenceMap() {
        return this._occurrencesMap || (this._occurrencesMap = new Map());
    }

    /**
     * Removes an occurrence from this recurring timespan's cached occurrences.
     * @param dateOrTimeSpan occurrence date or occurrence TimeSpan
     * @internal
     * @category Recurrence
     */
    removeOccurrence(dateOrTimeSpan, eventStore = this.eventStore) {
        const date = dateOrTimeSpan.isTimeSpan ? dateOrTimeSpan.occurrenceDate : dateOrTimeSpan;

        // Clear the occurrences *if* we are in an EventStore.
        eventStore?.globalOccurrences.delete(this.createRecurrenceKey(date));
        // Remove occurrence from its by-startDate cache
        this.occurrenceMap.delete(DateHelper.makeKey(date));
    }

    /**
     * Removes all cached occurrences on or after the passed date from this recurring timespan's cached occurrences.
     * @internal
     * @category Recurrence
     */
    removeOccurrencesFrom(date) {
        this.occurrenceMap.forEach((occurrence, dateKey) => {
            if (DateHelper.parseKey(dateKey) >= date) {
                this.removeOccurrence(occurrence);
            }
        });
    }

    /**
     * Removes this recurring timespan's cached occurrences.
     * @internal
     * @category Recurrence
     */
    removeOccurrences(eventStore) {
        // This recurring event must also be removed from the occurrenceMap if it's there
        // So insert it as the first element. Can also be found from the store's global occurrence
        // Map using [...this.eventStore.globalOccurrences.keys()].filter(e => e.startsWith(`_generated:${this.id}`))
        [this, ...this.occurrences].forEach(occurrence => this.removeOccurrence(occurrence, eventStore));
    }

    /**
     * The method is triggered when the timespan recurrence settings get changed.
     * It updates the {@link #field-recurrenceRule} field in this case.
     * @internal
     * @category Recurrence
     */
    onRecurrenceChanged() {
        this.recurrenceRule = this.recurrence?.rule || null;
    }

    sanitizeRecurrenceData(recurrence = this.recurrence) {
        // We must remove all occurrences whenever the recurrence changes
        this.removeOccurrences();

        // Remove all exceptionsDates that our owning timeSpan had that are
        // now after our end date and therefore redundant.
        if (recurrence.endDate) {
            const
                endDate            = DateHelper.clearTime(recurrence.endDate),
                { exceptionDates } = this;

            // If we had any exceptions on or after this date, remove them.
            if (exceptionDates) {
                for (const dateKey in exceptionDates) {
                    const exceptionDate = DateHelper.parseKey(dateKey);

                    if (exceptionDate >= endDate) {
                        delete exceptionDates[dateKey];
                    }
                }
            }
        }
    }

    /**
     * The original {@lScheduler.model.TimeSpan#field-startDate startDate} of this event before any modifications
     * took place. Used by {@link #function-removeOccurrence} and {@link #function-detachFromRecurringEvent}
     * @internal
     * @readonly
     * @category Recurrence
     */
    get occurrenceDate() {
        return this.meta.modified?.startDate || this.startDate;
    }

    /**
     * If this event is an {@link #property-isOccurrence occurrence} of a recurring event, then this
     * property yields its zero-based occurrence index in the sequence.
     * @property {Number}
     * @readonly
     * @category Recurrence
     */
    get occurrenceIndex() {
        return AbstractRecurrenceIterator.getOccurrenceIndex(this);
    }

    /**
     * Builds an occurrence of this recurring event by cloning the timespan data.
     * The method is used internally by the __RecurringTimeSpans__ mixin.
     * Override it if you need to customize the generated occurrences.
     *
     * If the date requested is the start date of the event sequence, `this`
     * record is returned. All runs of recurring events begin with the base record.
     * @param  {Date} occurrenceDate The occurrence start date.
     * @param  {Boolean} isFirst `true` if this is the first occurrence.
     * @returns {Scheduler.model.TimeSpan} The occurrence.
     * @internal
     * @category Recurrence
     */
    buildOccurrence(occurrenceDate, isFirst) {
        const
            me                    = this,
            {
                occurrenceMap,
                recurrence,
                meta
            }                     = me,
            globalOccurrences     = me.eventStore?.globalOccurrences,
            occurrenceKey         = DateHelper.makeKey(occurrenceDate),
            id                    = me.createRecurrenceKey(occurrenceDate, occurrenceKey),
            onStartDate           = !(occurrenceDate - me.startDate),
            { fieldMap }          = me.constructor;

        // An occurrence has a unique ID which identifies it by its base recurring event and its time.
        let occurrence   = globalOccurrences?.get(id),
            { duration } = me;

        // If there's no duration, or it's an all day event (which makes the event ceil and floor its
        // start and end dates, but does *NOT* as of 27/5/2020 adjust its duration) then
        // we calculate the duration of the occurrence.
        if (me.endDate && (me.allDay || !duration)) {
            duration = DateHelper.as(me.durationUnit, me.endDate.getTime() - me.startDate.getTime());
        }

        // Don't let NaN in record data
        const occurrenceEndDate = duration !== undefined ? DateHelper.add(occurrenceDate, duration, me.durationUnit) : undefined;

        if (!occurrence) {
            // If this is the first occurrence (start times may not match), or it's right on the start
            // then this recurring event *IS* the occurrence
            if (isFirst || onStartDate) {
                occurrence = me;

                // We are the first occurrence.
                // But if our start time is not as the rule requires, adjust ourselves
                // This must not cause the recurrence record to be synced with this.
                if (!onStartDate && me.recurrence?.timeZone == null) {
                    me.preventRecurrenceSync = true;

                    // We are setting this flag here to make sure we don't invalidate a recurring record in Engine when
                    // data is still being written to the records
                    me._preventInvalidation  = me.project?.isWritingData;
                    me.setStartEndDate(occurrenceDate, occurrenceEndDate, true);

                    // Resetting the flag
                    me._preventInvalidation  = false;
                    me.preventRecurrenceSync = false;

                    // Since we've changed the event start date the recurrence "Days"/"MonthDays"/"Months"
                    // might get redundant in case the event start date matches the fields values
                    // Calling recurrence sanitize() will clean the fields in this case.
                    recurrence.suspendTimeSpanNotifying();
                    recurrence.sanitize();
                    recurrence.resumeTimeSpanNotifying();
                }

                // Either way, because of adjustment above, or initial correctness, we are in sync
                // with our recurrence rule. A RecurrenceIterator is now able to calculate a correct
                // UNTIL date from a COUNT value. See AbstractRecurrenceIterator#processIterationConfig
                meta.isSyncedWithRule = true;
            }
            // Generate an occurrence which references this as its parent
            else {
                occurrence = me.copy(
                    {
                        [fieldMap.id.dataSource]        : id,
                        [fieldMap.startDate.dataSource] : occurrenceDate,
                        [fieldMap.endDate.dataSource]   : occurrenceEndDate,
                        [fieldMap.duration.dataSource]  : duration,
                        constraintDate                  : null,
                        constraintType                  : null
                    },
                    { creatingOccurrence : true }
                );

                occurrence.recurringTimeSpan = me;
            }

            globalOccurrences?.set(id, occurrence);

            // A recurring timespan keeps a by-startDate index of occurrences.
            // And itself will be among those.
            occurrenceMap.set(occurrenceKey, occurrence);
        }

        return occurrence;
    }

    createRecurrenceKey(date = this.startDate, dateKey = null) {
        return `_generated:${this.id}:${dateKey || DateHelper.makeKey(date)}`;
    }

    // Converts this occurrence to a new "master" event
    convertToRealEvent(wasSet, silent) {
        if (!this.isOccurrence) {
            return;
        }

        const
            me = this,
            {
                recurringTimeSpan,
                resources,
                occurrenceIndex,
                recurrence
            }               = me,
            count           = recurrence && recurringTimeSpan.recurrence.count,
            // resourceRecords is a temporary property of occurrence events to handle cases
            // if only resources has been updated. (change only resources won't mark record as dirty)
            newResource     = wasSet?.resourceRecords?.value || me.data.newResource;

        recurringTimeSpan.beginBatch();

        me.detachFromRecurringEvent();

        me.internalClearChanges();

        // Must silently set our own ID, not be the key generated from our parent id and occurrence date.
        // Must not result in the id field being in the modified state.
        me._id = me.generateId(recurringTimeSpan.eventStore);
        me.setData('id', me._id);

        if (newResource) {
            // clear resourceId to avoid auto-adding to assignmentStore, it is handled manually below
            delete me.data.resourceId;
        }

        // The impending changes to the former parent recurring event trigger a full refresh.
        recurringTimeSpan.eventStore.add(me, silent);

        // Ensure that the original count is honoured.
        // If we are the 8th occurrence of 10, OUR repeat count must be 3.
        if (count) {
            me.recurrence.count = count - occurrenceIndex;
        }

        if (newResource || resources?.length) {
            me.assign(newResource || resources);
        }

        // remove data after apply
        if (newResource) {
            delete me.data.resourceRecords;
        }
        if (wasSet) {
            delete wasSet.resourceRecords;
        }

        // Any change to a recurring events triggers a store refresh event.
        recurringTimeSpan.endBatch();
    }

    afterChange(toSet, wasSet, silent, ...args) {
        const
            me             = this,
            { eventStore } = me;

        // reset cached recurrence instance in case "recurrenceRule" is changed
        if ('recurrenceRule' in wasSet) {
            me._recurrence = null;

            // If we are a recurring event, we must be in the recurringEvents cache.
            // If we are *not* a recurring event, we must *not* be in there.
            // An event not yet in a store (eg dragging to create) won't have an eventStore.
            eventStore?.recurringEvents[wasSet.recurrenceRule.value ? 'add' : 'delete'](me);
        }

        // Any change to an occurrence adds it to an event store, at which point
        // it ceases to be an occurrence.
        //
        // If it has a recurrenceRule it becomes the start of a new recurring event series,
        // and the old owning recurring event stops on the day before.
        //
        // If it has no recurrenceRule, it becomes an exception to its owning recurring event.
        if (me.isOccurrence) {
            me.convertToRealEvent(wasSet, silent);
        }
        else {
            // If it's a recurring base that's in the store (Not a tentative, dragging event)
            // and is being rescheduled, ensure that its BYXXX rule modifiers
            // match the new reality if configured to do so.
            if (!me.preventRecurrenceSync && ('startDate' in wasSet) && me.isRecurring && eventStore?.autoAdjustRecurrence) {
                const
                    { recurrence } = me,
                    { startDate }  = me,
                    oldStartDate   = wasSet.startDate.oldValue,
                    oldMonthDate   = startDate.getDate(oldStartDate),
                    oldMonthWeek   = Math.ceil(oldMonthDate / 7),
                    monthDate      = startDate.getDate(),
                    dayNumber      = startDate.getDay(),
                    dayName        = RecurrenceDayRuleEncoder.encodeDay(dayNumber),
                    monthNumber    = startDate.getMonth() + 1,
                    monthWeek      = Math.ceil(monthDate / 7),
                    days           = recurrence.days?.slice(),
                    months         = recurrence.months?.slice(),
                    monthDays      = recurrence.monthDays?.slice(),
                    positions      = recurrence.positions?.slice();

                // We will update multiple fields, the rule must not be re-evaluated until we are finished
                recurrence.beginBatch();

                // The change to a new date clashes with one of our occurrences.
                // The filter needs to be there to filter out iterator bugs.
                if (!me.getOccurrencesForDateRange(startDate, DateHelper.add(startDate, 1)).filter(r => !(r.startDate - startDate))?.length) {
                    // Moved into empty space.
                    // If we had explicit days and monthDays, the old one needs to be replaced by the new one
                    // BYDAY=
                    if (days?.length) {
                        ArrayHelper.remove(days, RecurrenceDayRuleEncoder.encodeDay(oldStartDate.getDay()));
                        days.push(dayName);
                        recurrence.days = days.sort();
                    }
                    // BYMONTHDAY=
                    if (monthDays?.length) {
                        ArrayHelper.remove(monthDays, oldStartDate.getDate());
                        monthDays.push(monthDate);
                        recurrence.monthDays = monthDays.sort();
                    }
                    // BYSETPOS=
                    if (positions?.length) {
                        ArrayHelper.remove(positions, oldMonthWeek);
                        positions.push(monthWeek);
                        recurrence.positions = [...new Set(positions)].sort();
                    }
                    // If we dragged the base out of its start month, we have to
                    // remove the old start month and add the new one (But dedupe it using Set)
                    if (months?.length) {
                        ArrayHelper.remove(months, oldStartDate.getMonth() + 1);
                        months.push(monthNumber);
                        recurrence.months = [...new Set(months)].sort();
                    }
                }

                // Flush changes which will update our recurrenceRule property
                // with its callback to our onRecurrenceChanged
                recurrence.endBatch();
            }

            // Setting a newException date must mark the exceptionDates as modified
            if ('newExceptionDate' in wasSet) {
                me.meta.modified.exceptionDates = true;
                delete me.meta.modified.newExceptionDate;

                // Remove any occurrence on that date from our by-startDate cache
                // and from the global occurrences cache
                me.removeOccurrence(wasSet.newExceptionDate.value);
            }
            // Clearing the recurrenceRule should also clear exceptionDates
            if (wasSet.recurrenceRule && !wasSet.recurrenceRule.value) {
                me.meta.modified.exceptionDates = true;
                me.exceptionDates = {};
            }
        }

        return super.afterChange(toSet, wasSet, silent, ...args);
    }

    /**
     * Detaches an occurrence from its owning recurring event so that it can be added to the eventStore
     * either as an exception, or as the start of a new recurring sequence.
     * @internal
     * @category Recurrence
     */
    detachFromRecurringEvent() {
        const
            me                                    = this,
            // For access further down, breaking the link involves engine if trying to get the occurrenceDate later,
            // resulting in the wrong date
            { recurringTimeSpan, occurrenceDate, startDate } = me;

        // Break the link
        me.recurringTimeSpan = null;

        // The occurrenceDate is injected into the data when an occurrence is created.
        // the recurringTimeSpan's afterChange will remove any cache occurrence
        // for this date; see above
        recurringTimeSpan.addExceptionDate(occurrenceDate);

        // If we still have a recurrenceRule, we're being promoted to be a new recurring event.
        // The recurrence setter applies the rule immediately to occurrences, so this will
        // always be correct.
        if (me.recurrenceRule) {
            // The RecurrenceModel removes occurrences and exceptions after this date
            recurringTimeSpan.recurrence.endDate = DateHelper.add(startDate, -1, 'minute');
        }
    }

    /**
     * The setter used by Model#inSet when {@link #function-addExceptionDate} is called.
     * Adding an exception must trigger change processing in a recurring event, so it must
     * be changed through a {@link Core.data.Model#function-set} call. Also, the change must be batchable
     * with other changes.
     * @private
     * @readonly
     * @category Recurrence
     */
    set newExceptionDate(date) {
        if (date) {
            const exceptionDates = this.exceptionDates || (this.exceptionDates = {});

            exceptionDates[DateHelper.makeKey(date)] = 1;
        }
    }

    /**
     * Adds an exception date that should be skipped when generating occurrences for the timespan.
     * The method adds an entry to the array kept in {@link #field-exceptionDates} field.
     * @param {Date} date Exception date
     * @category Recurrence
     */
    addExceptionDate(newExceptionDate) {
        this.set({
            newExceptionDate
        });
    }

    /**
     * Does this recurring event have an exception on the passed date.
     * @param {Date} date The date to find an exception for.
     * @returns {Boolean} `true` if the event has an exception starting on the passed date.
     * @category Recurrence
     */
    hasException(date) {
        return Boolean(this.exceptionDates?.[DateHelper.makeKey(date)]);
    }

    // Support TimeZonedDateMixin API
    convertToTimeZone(timeZone, ...args) {
        if (this.timeZone !== timeZone) {
            if (this.isRecurring) {
                // occurrences will be re-built with proper dates
                this.removeOccurrences();
            }
        }

        super.convertToTimeZone(timeZone, ...args);
    }
};
